<template>
  <div class="checklist-modal" :readonly="readonly">
    <div v-if="type === 'default'" @click="open" class="checklist-modal__input">
      <slot name="input" :items="valueComp">
        <slot
          v-for="item in valueComp.length ? valueComp : [{}]"
          :name="hasValue ? 'item' : 'empty'"
          :item="item"
          scope="input"
        />
      </slot>
      <input
        type="text"
        class="checklist-modal__input-element"
        :value="valueComp.map((v, i) => i).join('-')"
        :required="required"
      />
    </div>
    <component :is="modalWrapper" class="checklist-modal__modal-wrapper">
      <ModalComponent :show.sync="show" clickaway class="checklist-modal__modal">
        <Card theme="flat" :title="title" class="checklist-modal__card">
          <template #default>
            <Loader :value="promises.fetch" theme="default" class="checklist-modal__scroller">
              <Checklist
                v-model="selectedComp"
                :items="itemsComp.map(transform)"
                :group-by="groupBy"
                :select-all="selectAll"
                :search="search"
              />
            </Loader>
          </template>
          <template #footer>
            <slot name="actions" />
            <ButtonComponent :label="$t('cancel')" @click="close" />
            <ButtonComponent
              :label="$t('confirm')"
              :icon="['fal', 'arrow-to-right']"
              :promise="promise"
              theme="primary"
              @click="confirm"
            />
          </template>
        </Card>
      </ModalComponent>
    </component>
  </div>
</template>

<script>
import { Debounce } from '@/utils'

import { ModalComponent } from 'vue-elder-modal'
import Checklist from './Checklist.vue'
import { InputComponent } from 'vue-elder-input'
import { LoaderComponent } from 'vue-elder-loader'
import Card from '@/components/Card'

export default {
  props: {
    title: String,
    search: Boolean,
    readonly: Boolean,
    required: Boolean,
    selectAll: Boolean,
    groupBy: Object,
    type: {
      type: String,
      enum: ['default', 'modal-only'],
      default: 'default',
    },
    filter: {
      type: Object,
      default: () => ({}),
    },
    promise: Promise,
    compare: {
      type: Function,
      default: (a, b) => {
        let getValue = x => (typeof x === 'string' ? x : x.id)
        return getValue(a) === getValue(b)
      },
    },
    isDisabled: {
      type: Function,
      default: () => false,
    },
    value: Array,
    transform: {
      type: Function,
      default: i => i,
    },
    items: {
      type: [Function, Array],
      required: true,
    },
    modalWrapper: {
      type: [String, Function],
      default: 'div',
    },
  },
  data() {
    return {
      show: false,
      fetchedItems: [],
      selected: [],
      promises: {
        fetch: null,
      },
      defaultFilter: {
        search: null,
      },
    }
  },
  watch: {
    show: {
      handler(val) {
        if (val)
          this.internalfetch(this.filterComp).then(() => {
            this.select(this.valueComp)
          })
        else {
          this.selected = []
        }
      },
      immediate: true,
    },
    filterComp: {
      handler: 'performQuery',
      deep: true,
    },
    promise(val) {
      if (!val) return
      val.then(() => this.close())
    },
  },
  computed: {
    filterComp() {
      return {
        ...this.defaultFilter,
        ...this.filter,
      }
    },
    selectedComp: {
      get() {
        return this.selected.map(s => {
          if (typeof s === 'string') return s
          return s.id
        })
      },
      set(val) {
        this.selected = this.itemsComp.filter(i => val.some(v => this.compare(v, i)))
      },
    },
    valueComp: {
      get() {
        return this.value || []
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    itemsComp() {
      return this.items instanceof Array ? this.items : this.fetchedItems
    },
    hasValue() {
      return this.valueComp.length
    },
    hasSearch() {
      return this.search && typeof this.items === 'function'
    },
  },
  methods: {
    performQuery: Debounce(function () {
      this.internalfetch(this.filterComp)
    }, 200),
    select(items) {
      items = items instanceof Array ? items : [items]
      items.forEach(item => {
        if (this.isDisabled(item)) return
        if (this.isSelected(item)) this.selected = this.selected.filter(i => !this.compare(i, item))
        else this.selected.push(item)
      })
    },
    isSelected(item) {
      return this.selected.find(i => this.compare(i, item))
    },
    open() {
      if (this.readonly) return
      this.show = true
    },
    close() {
      this.show = false
    },
    confirm() {
      this.$emit('input', this.selected)
      if (!('promise' in this.$attrs)) this.close()
    },
    internalfetch(filter) {
      if (this.items instanceof Array) return Promise.resolve(this.items)
      return (this.promises.fetch = this.items(filter).then(data => (this.fetchedItems = data)))
    },
  },
  components: {
    Card,
    ModalComponent,
    InputComponent,
    LoaderComponent,
    Checklist,
  },
}
</script>

<style lang="scss">
.checklist-modal {
  &__input {
    cursor: pointer;
    position: relative;
    height: 100%;

    &-element {
      position: absolute;
      width: 100%;
      height: 100%;
      margin: 0 !important;
      top: 0;
      opacity: 0;
      pointer-events: none;
      outline: none !important;
    }

    & > * + * {
      margin-top: 1rem;
    }
  }

  &__scroller {
    max-height: 350px;
    overflow-y: scroll;

    @include scrollShadow();
  }

  &__card {
    width: 100%;
    max-width: 600px;
  }

  &__modal-card {
    & > .card__content {
      display: flex;
    }
  }

  &__modal-content {
    flex-grow: 1;
  }

  .card {
    &__title {
      padding-bottom: 0.5rem;
      border-bottom: 1px solid $border-color;
    }
    &__footer {
      & > div {
        justify-content: center;
      }
    }
  }
}
</style>
